import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  computed,
  input,
  signal
} from '@angular/core';
import { cleanJsonString } from '~ui-kit/core/utils';

let nextId = 0;

@Component({
  selector: 'select-option',
  standalone: true,
  host: {
    role: 'option',
    '[attr.aria-selected]': 'isSelected().toString()',
    '[attr.aria-disabled]': 'isDisabled().toString()',
    '[attr.data-value]': 'formattedValue()',
    '[attr.aria-hidden]': 'isHidden()'
  },
  template: `<li
    class="select-option"
    [class.select-option--selected]="isSelected()"
    [class.select-option--hidden]="isHidden()"
  >
    @if (isMultiple()) {
      <span class="select-option__check-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="11"
          height="8"
          viewBox="0 0 10.813 7.958"
        >
          <path
            d="M-9727.816,4720.984l2.441,2.441,5.544-5.544"
            transform="translate(9729.23 -4716.467)"
            fill="none"
            stroke="#199e46"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
          />
        </svg>
      </span>
    }
    <ng-content></ng-content>
  </li>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectOptionComponent implements OnChanges {
  value = input<unknown>();
  selected = input(false);
  disabled = input(false);
  hidden = input(false);

  isDisabled = signal(false);
  isSelected = signal(false);
  uniqueId = `select-option-${++nextId}`;
  isMultiple = signal(false);
  isHidden = signal(false);

  formattedValue = computed(() => {
    return this._convertToString(this.value());
  });

  constructor(
    private _elementRef: ElementRef<HTMLElement>,
    private _destroyRef: DestroyRef,
    private _cdr: ChangeDetectorRef
  ) {}

  get host(): HTMLElement {
    return this._elementRef.nativeElement;
  }

  get destroyRef(): DestroyRef {
    return this._destroyRef;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // console.log(changes, this.host);
    if ('disabled' in changes) {
      this.isDisabled.set(this.disabled());
    }

    if ('selected' in changes) {
      this.isSelected.set(this.selected());
    }

    if ('hidden' in changes) {
      this.isHidden.set(this.hidden());
    }
  }

  private _convertToString(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'bigint') {
      return value.toString();
    }

    if (typeof value === 'symbol') {
      return value.toString();
    }

    if (typeof value === 'undefined') {
      return this.host.innerText;
    }

    if (value === null) {
      return 'null';
    }

    if (typeof value === 'object') {
      try {
        const jsonString = JSON.stringify(value);
        return cleanJsonString(jsonString, true);
      } catch (error) {
        return this.host.innerText;
      }
    }

    return String(value);
  }

  detectChange(): void {
    this._cdr.detectChanges();
  }
}
